import { useState, useEffect, useCallback } from "react";
import { v2_fetch_user_details, v2_personalInformation } from "../actions/v2/user";
import constant from "../constant";

/**
 * ✅ Custom Hook: useUserDetails
 * Fetches and caches user info in sessionStorage
 */
export const useUserDetails = () => {
  const [user, setUser] = useState(() => {
    try {
      const cached = sessionStorage.getItem(constant.keys.userDetails);
      return cached ? JSON.parse(cached) : null;
    } catch {
      return null;
    }
  });
  const [loading, setLoading] = useState(!user);
  const [error, setError] = useState(null);

  /**
   * 🔹 Fetch user details from API
   */
  const fetchUserDetails = useCallback(async () => {
    try {
      setLoading(true);
      const res = await v2_personalInformation();
      if (res.status && res.result) {
        setUser(res.result);
        sessionStorage.setItem("userDetails", JSON.stringify(res.result));
        setError(null);
      } else {
        setError(res.message || "Failed to fetch user details");
      }
    } catch (err) {
      console.error("❌ Error fetching user details:", err);
      setError(err.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  }, []);

  /**
   * 🔹 Refresh data from API (force update)
   */
  const refreshUser = useCallback(async () => {
    sessionStorage.removeItem("userDetails");
    await fetchUserDetails();
  }, [fetchUserDetails]);

  /**
   * 🔹 Clear user data (e.g., on logout)
   */
  const clearUser = useCallback(() => {
    sessionStorage.removeItem("userDetails");
    setUser(null);
  }, []);

  // Load from sessionStorage or fetch from API (only on mount)
  useEffect(() => {
    if (!user) fetchUserDetails();
  }, [user, fetchUserDetails]);

  return {
    user,
    loading,
    error,
    refreshUser,
    clearUser,
    setUser,
  };
};
